import { inject, Injectable } from '@angular/core';
import { AccountsService, ActivationStatus } from '@vendasta/accounts';
import { AccountDates, AccountsService as LegacyAccountsService } from '@vendasta/accounts/legacy';
import { AppKey, PartnerApiService } from '@galaxy/marketplace-apps';
import { BehaviorSubject, catchError, combineLatest, filter, map, Observable, of, Subject, switchMap, tap } from 'rxjs';
import { SimpleAccountDate, SimpleApp, SimpleProduct } from './interfaces';
import { isBefore, isValid } from 'date-fns';

@Injectable()
export class CrmMarketplaceProductsAssociationCardService {
  private readonly partnerId$$ = new Subject<string>();
  private readonly accountGroupId$$ = new Subject<string>();
  private readonly marketId$$ = new Subject<string>();
  private readonly getProductsFailed$$ = new BehaviorSubject(false);

  readonly products$: Observable<SimpleProduct[]>;
  readonly getProductsFailed$: Observable<boolean> = this.getProductsFailed$$.asObservable();

  setPartnerId(partnerId: string): void {
    this.partnerId$$.next(partnerId);
  }

  setAccountGroupId(accountGroupId: string): void {
    this.accountGroupId$$.next(accountGroupId);
  }

  setMarketId(marketId: string): void {
    this.marketId$$.next(marketId);
  }

  private readonly accountsService = inject(AccountsService);
  private readonly legacyAccountsService = inject(LegacyAccountsService);
  private readonly partnerApiService = inject(PartnerApiService);

  constructor() {
    const activeProducts$ = this.accountGroupId$$.pipe(
      switchMap((agid) => {
        if (!agid) {
          return of([]);
        }
        return this.accountsService.getMultiActivationStatuses([agid]);
      }),
      map((products) =>
        products
          .sort((a, b) => (a?.productId > b?.productId ? 1 : -1))
          .filter((p) => p?.productId && p.status === ActivationStatus.ACTIVATION_STATUS_ACTIVATED)
          .map((p) => ({
            productId: p.productId,
            editionId: p.editionId || '',
            isTrial: !!p.isTrial,
            quantity: p.count || 1,
          })),
      ),
    );

    const groupedProducts$ = combineLatest([this.partnerId$$, this.marketId$$, activeProducts$]).pipe(
      filter(([partnerId, marketId]) => !!partnerId && !!marketId),
      switchMap(([partnerId, marketId, products]) => this.fillAppDataAndGroupAddons(partnerId, marketId, products)),
    );

    const accountDates$ = this.accountGroupId$$.pipe(
      switchMap((agid) => {
        if (!agid) {
          return of([]);
        }
        return this.legacyAccountsService.listAccountDates(agid, '', 1000);
      }),
      catchError((err) => {
        console.error(err);
        return of([]);
      }),
    );

    this.products$ = combineLatest([groupedProducts$, accountDates$]).pipe(
      map(([products, dates]) => this.fillProductDates(products, dates)),
      tap(() => this.getProductsFailed$$.next(false)),
      catchError((err) => {
        console.error(err);
        this.getProductsFailed$$.next(true);
        return of([]);
      }),
    );
  }

  fillAppDataAndGroupAddons(
    partnerId: string,
    marketId: string,
    products: SimpleProduct[],
  ): Observable<SimpleProduct[]> {
    if (!products?.length) {
      return of([]);
    }

    const productAppKeys = products.map(
      (product) => new AppKey({ appId: product.productId, editionId: product.editionId }),
    );
    return this.partnerApiService
      .getMultiApp({
        partnerId: partnerId,
        marketId: marketId,
        appKeys: productAppKeys,
        includeNotEnabled: true,
      })
      .pipe(
        map(({ apps }) => {
          const productsToAddons: Record<string, SimpleProduct[]> = {};
          apps.forEach((a) => {
            const app = new SimpleApp(a);
            if (app.isAddon()) {
              productsToAddons[app.getParentId()] = [];
            }
          });

          return products.flatMap((product, idx) => {
            const app = new SimpleApp(apps[idx]);
            product.name = app.getFullName();
            product.iconUrl = app.getIconUrl();

            if (app.isAddon() && app.getParentId()) {
              productsToAddons[app.getParentId()].push(product);
              return [];
            }
            product.addons = productsToAddons[product.productId];
            product.entryUrl = app.getEntryUrl(product.productId);
            return [product];
          });
        }),
      );
  }

  private fillProductDate(product: SimpleProduct, date: AccountDates | undefined): void {
    if (!date) {
      return;
    }

    const simpleDate = new SimpleAccountDate(date);
    product.activationDate = simpleDate.getActivationDate();
    product.trialEnded = simpleDate.isTrialExpired();
    product.trialExpirationDate = simpleDate.getTrialExpirationDate();
    product.trialDaysRemaining = simpleDate.getTrialDaysRemaining();
    product.renewalDate = simpleDate.getRenewalDate();
    product.commitmentDate = simpleDate.getCommitmentDate();
  }

  private dateIsForProduct(date: AccountDates, productId: string, addonId?: string): boolean {
    const isDeactivated = (d: AccountDates) =>
      isValid(d.deactivation) && d.deactivation.getTime() > 0 && isBefore(d.deactivation, new Date());

    if (addonId) {
      return date.appId === productId && date.addonId === addonId && !isDeactivated(date);
    }
    return date.appId === productId && !date.addonId && !isDeactivated(date);
  }

  private findDateForProduct(
    productId: string,
    addonId: string,
    quantity: number,
    dates: AccountDates[],
  ): AccountDates | undefined {
    if (quantity > 1) {
      let foundCount = 0;
      let oldestDate = undefined;
      for (const date of dates) {
        if (this.dateIsForProduct(date, productId, addonId)) {
          if (!oldestDate || isBefore(date.activation, oldestDate.activation)) {
            oldestDate = date;
          }

          foundCount += 1;
          if (foundCount >= quantity) {
            break;
          }
        }
      }
      return oldestDate;
    }

    return dates.find((d) => this.dateIsForProduct(d, productId, addonId));
  }

  fillProductDates(products: SimpleProduct[], dates: AccountDates[]): SimpleProduct[] {
    if (!products?.length || !dates?.length) {
      return products;
    }

    for (const product of products) {
      const productDate = this.findDateForProduct(product.productId, '', product.quantity, dates);
      this.fillProductDate(product, productDate);

      for (const addon of product.addons || []) {
        const addonDate = this.findDateForProduct(product.productId, addon.productId, addon.quantity, dates);
        this.fillProductDate(addon, addonDate);
      }
    }

    return products;
  }
}
